import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { usePartnerAgreement } from '~domains/partner-application/entities/agreement';
import { partnerApplicationKeys } from '~domains/partner-application/entities/application';
import { PdfPreview } from '~domains/partner-application/widgets/pdf-view';

import { useUserPhone } from '~platform/auth';
import {
  ConfirmOtpWidget,
  useGetMerchantOrder,
} from '~platform/confirmation-service';

import { ROUTES } from '~shared/config/routes';
import { ErrorScreen } from '~shared/ui/error-screen';
import { Footer } from '~shared/ui/footer';
import { Loader } from '~shared/ui/loader';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { logger } from '@tinkoff/logger';

import { toast } from '@breezeventures/ui-kit/toast';
import { useQueryClient } from '@tanstack/react-query';

import { SignedScreen } from './screens';

const NO_NUMBER_ERROR_MESSAGE =
  "No user's phone number while signing the agreement.";

export const PartnerApplicationSignPage = () => {
  const phoneNumber = useUserPhone();
  const navigate = useNavigate();
  const { id: applicationId } = useParams();
  const [state, setState] = useState<'view-agreement' | 'otp' | 'signed'>(
    'view-agreement'
  );
  const queryClient = useQueryClient();

  const agreementUrlQuery = usePartnerAgreement({
    applicationId,
  });

  const orderQuery = useGetMerchantOrder({
    enabled: false,
    params: { applicationId },
  });

  const onSignAgreement = useCallback(async () => {
    if (!phoneNumber) {
      logger.error(NO_NUMBER_ERROR_MESSAGE);
    }

    await orderQuery.refetch();
    setState('otp');
  }, [orderQuery, phoneNumber]);

  const onSignSuccess = useCallback(async () => {
    await queryClient.resetQueries({
      queryKey: partnerApplicationKeys.byPerson(),
    });
    setState('signed');
    toast('The document has been successfully signed!', {
      type: 'info',
    });
  }, [queryClient]);

  const onClose = () => navigate(ROUTES.partner.applicationMenu);

  if (state === 'view-agreement') {
    if (agreementUrlQuery.data) {
      return (
        <Screen
          header={<ScreenHeader onClose={onClose} />}
          footer={
            <Footer
              submitButtonText="Sign the agreement"
              onSubmit={onSignAgreement}
              isLoading={orderQuery.isLoading}
            />
          }
          withoutPadding
        >
          <PdfPreview
            pdfUrl={agreementUrlQuery.data}
            title="Partner agreement document"
          />
        </Screen>
      );
    }

    if (agreementUrlQuery.isError) {
      return <ErrorScreen message="Failed to load agreement." />;
    }

    return <Loader />;
  }

  if (state === 'otp') {
    if (orderQuery.data && applicationId) {
      return (
        <Screen
          header={
            <ScreenHeader
              onBack={() => setState('view-agreement')}
              onClose={onClose}
            />
          }
        >
          {phoneNumber ? (
            <ConfirmOtpWidget
              orderId={orderQuery.data.order.id}
              phoneNumber={phoneNumber}
              onSuccess={onSignSuccess}
              submitButtonText="Sign the agreement"
            />
          ) : (
            <ErrorScreen
              asChild
              message={
                <>
                  Cannot get your phone number.
                  <br /> Please, try again later
                </>
              }
            />
          )}
        </Screen>
      );
    }

    if (orderQuery.isError) {
      return <ErrorScreen message="Failed to get order." />;
    }

    return <Loader />;
  }

  if (state === 'signed') {
    return <SignedScreen />;
  }

  return null;
};
